/**
 * Audio feedback for the alert notification system.
 *
 * - `playAlertChime()` — short two-tone chime, used for new
 *   critical/warning toasts. The chime is synthesised on demand
 *   via the Web Audio API (no embedded audio file) so the bundle
 *   stays dependency-free.
 * - Mute state is persisted in localStorage so the user's choice
 *   survives a page refresh (and a session restart). Cross-tab
 *   synchronisation is provided by the `storage` event so the
 *   fire-alarm siren effect can react when another tab toggles
 *   the master mute.
 * - Plays through the console's one shared AudioContext
 *   (`audio-context.ts`), so the tap that unlocks it unlocks the fire
 *   siren too. While the browser holds it suspended, `playAlertChime`
 *   is a no-op: a chime scheduled on a suspended context is not lost,
 *   it waits, and every chime queued that way played at once on the
 *   first tap.
 */
import { sharedAudioContext } from './audio-context';

let cachedGain: GainNode | null = null;
let cachedGainCtx: AudioContext | null = null;

const MUTE_KEY = 'nx:alerts:muted';
type MuteListener = () => void;
const muteListeners = new Set<MuteListener>();

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== MUTE_KEY) return;
    for (const l of muteListeners) l();
  });
}

export function isMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    // ignore
  }
  for (const l of muteListeners) l();
}

/** Subscribe to mute-state changes (covers both in-process calls to
 *  `setMuted` and cross-tab `storage` events). Returns an unsubscribe
 *  function. Used by the fire-alarm provider so toggling the master
 *  mute while a siren is active takes effect immediately. */
export function subscribeMute(listener: MuteListener): () => void {
  muteListeners.add(listener);
  return () => muteListeners.delete(listener);
}

function chime(): void {
  const ctx = sharedAudioContext();
  if (!ctx) return;
  if (ctx.state !== 'running') {
    // Worth asking: it works where the page already has the user's
    // permission (Chrome after a click), and is refused harmlessly
    // where it does not.
    if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
    return;
  }
  if (!cachedGain || cachedGainCtx !== ctx) {
    cachedGain = ctx.createGain();
    cachedGain.gain.value = 0.15;
    cachedGain.connect(ctx.destination);
    cachedGainCtx = ctx;
  }
  const now = ctx.currentTime;
  const tones: Array<[number, number, number]> = [
    [880, now, 0.15],
    [1320, now + 0.18, 0.25],
  ];
  for (const [freq, start, dur] of tones) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = 0;
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(0.4, start + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.connect(g);
    g.connect(cachedGain);
    osc.start(start);
    osc.stop(start + dur + 0.02);
  }
}

/** The chime for a new critical or warning toast, unless muted. */
export function playAlertChime(): void {
  if (isMuted()) return;
  chime();
}

/** The chime the "Tap to enable alert sound" chip plays once the tap
 *  has unlocked the context, so the person hears that sound works. It
 *  plays even with the chime muted: they asked for it, and the chip
 *  shows while muted only when the fire siren is set to sound anyway. */
export function playTestChime(): void {
  chime();
}